import { useEffect, useState, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, FileAudio, FileVideo, FileImage, FileText, Archive, File as FileIcon, Music2, Play } from "lucide-react";

const CATEGORIES = [
  { v: "exclusive_song", l: "Exclusive Song" },
  { v: "behind_scenes", l: "Behind the Scenes" },
  { v: "studio_session", l: "Studio Session" },
  { v: "unreleased", l: "Unreleased" },
  { v: "wallpaper", l: "Wallpaper" },
  { v: "commentary", l: "Commentary" },
  { v: "early_access", l: "Early Access" },
];

const KIND_ICON = {
  audio: FileAudio, video: FileVideo, image: FileImage,
  pdf: FileText, zip: Archive, other: FileIcon,
};

function humanSize(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

export default function VaultManager() {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [v, t] = await Promise.all([api.get("/admin/vault"), api.get("/tiers")]);
    setItems(v.data || []); setTiers(t.data || []); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ title: "", description: "", category: "exclusive_song", min_tier_slug: "gold", min_points: 0, is_visible: true, cover_url: "", files: [] });
  const openEdit = async (v) => {
    // refetch to get latest files[]
    const { data } = await api.get("/admin/vault");
    const fresh = (data || []).find((x) => x.id === v.id) || v;
    setEditing({ ...fresh, files: fresh.files || [] });
  };

  const saveMeta = async (v) => {
    const payload = {
      title: v.title || "", description: v.description || "",
      category: v.category || "exclusive_song",
      cover_url: v.cover_url || "",
      file_url: v.file_url || "", image_url: v.image_url || "", external_url: v.external_url || "",
      min_tier_slug: v.min_tier_slug || "gold", min_points: Number(v.min_points) || 0,
      unlock_slug: v.unlock_slug || "", is_visible: v.is_visible !== false,
      duration: v.duration || "", release_date: v.release_date || "",
    };
    try {
      if (v.id) {
        await api.put(`/admin/vault/${v.id}`, payload);
      } else {
        const { data } = await api.post("/admin/vault", payload);
        setEditing({ ...data, files: [] });
      }
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vault item?")) return;
    await api.delete(`/admin/vault/${id}`);
    toast.success("Deleted");
    load();
  };

  if (loading) return <div className="text-white/40">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="vault-manager">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl text-white tracking-wider">Exclusive Vault</h2>
          <p className="text-white/40 text-xs mt-1">Drag files in. Cover art, MP3, WAV, MP4, PDF, ZIP, images — all supported. Members stream/download based on their tier.</p>
        </div>
        <button onClick={openNew} data-testid="new-vault-item"
                className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> New Item
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 ? (
          <div className="col-span-full text-center p-8 border border-white/5 rounded text-white/40 text-xs">No vault items yet.</div>
        ) : items.map((v) => (
          <div key={v.id} className="bg-[#0A0A0A] border border-white/5 rounded p-3" data-testid={`vault-row-${v.id}`}>
            <div className="aspect-video bg-black rounded overflow-hidden mb-3 relative">
              {(v.cover_url || v.image_url) ? (
                <img src={(v.cover_url || v.image_url).startsWith('http') ? (v.cover_url || v.image_url) : `${process.env.REACT_APP_BACKEND_URL}${(v.cover_url || v.image_url)}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Music2 className="w-8 h-8 text-white/20" />
                </div>
              )}
              <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-1 bg-black/70 text-[#D4AF37] rounded">{(v.files || []).length} files</span>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{v.category}</p>
            <p className="text-white text-sm font-medium">{v.title}</p>
            <p className="text-white/40 text-[11px] mt-1">Tier: {v.min_tier_slug} · {v.min_points || 0} pts</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => openEdit(v)} data-testid={`vault-edit-${v.id}`} className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white">Edit</button>
              <button onClick={() => del(v.id)} className="text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300 ml-auto"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditorModal
          item={editing}
          tiers={tiers}
          onClose={() => { setEditing(null); load(); }}
          onSaveMeta={saveMeta}
        />
      )}

      <style>{`.input-dark { background:#000; border:1px solid rgba(255,255,255,0.1); color:#fff; padding:10px 12px; font-size:13px; border-radius:4px; outline:none; } .input-dark:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}

function EditorModal({ item, tiers, onClose, onSaveMeta }) {
  const [v, setV] = useState(item);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => { setV(item); }, [item?.id]);

  const uploadFiles = async (fileList) => {
    if (!v.id) {
      toast.error("Save the item first (title required) — then upload files.");
      return;
    }
    for (const file of Array.from(fileList)) {
      const tempId = `${file.name}-${Date.now()}`;
      setUploading((u) => [...u, { id: tempId, name: file.name, size: file.size }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/admin/vault/${v.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setV((cur) => ({ ...cur, files: [...(cur.files || []), data], cover_url: cur.cover_url || (data.kind === "image" && !cur.cover_url ? `/api/files/${data.storage_path}` : cur.cover_url) }));
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        toast.error(`Failed: ${file.name} · ${e.response?.data?.detail || e.message}`);
      } finally {
        setUploading((u) => u.filter((x) => x.id !== tempId));
      }
    }
  };

  const removeFile = async (fid) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await api.delete(`/admin/vault/${v.id}/files/${fid}`);
      setV((cur) => ({ ...cur, files: (cur.files || []).filter((f) => f.id !== fid) }));
      toast.success("Removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setAsCover = async (fileObj) => {
    if (fileObj.kind !== "image") { toast.error("Only image files can be set as cover"); return; }
    const cover = `/api/files/${fileObj.storage_path}`;
    setV((cur) => ({ ...cur, cover_url: cover }));
    try {
      await api.put(`/admin/vault/${v.id}`, {
        title: v.title, description: v.description || "", category: v.category || "exclusive_song",
        cover_url: cover, file_url: v.file_url || "", image_url: v.image_url || "", external_url: v.external_url || "",
        min_tier_slug: v.min_tier_slug || "gold", min_points: Number(v.min_points) || 0,
        unlock_slug: v.unlock_slug || "", is_visible: v.is_visible !== false,
        duration: v.duration || "", release_date: v.release_date || "",
      });
      toast.success("Cover updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 max-w-3xl w-full my-8 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="font-heading text-xl text-white">{v.id ? "Edit" : "New"} Vault Item</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white">✕</button>
        </div>

        {/* Metadata */}
        <div className="space-y-3">
          <input className="input-dark w-full" placeholder="Title" value={v.title || ""}
                 onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="vault-title" />
          <textarea className="input-dark w-full" rows={2} placeholder="Description"
                    value={v.description || ""} onChange={(e) => setV({ ...v, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-dark" value={v.category} onChange={(e) => setV({ ...v, category: e.target.value })} data-testid="vault-category">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
            <select className="input-dark" value={v.min_tier_slug} onChange={(e) => setV({ ...v, min_tier_slug: e.target.value })} data-testid="vault-tier">
              {tiers.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
            </select>
            <input className="input-dark" type="number" placeholder="Min points" value={v.min_points || 0}
                   onChange={(e) => setV({ ...v, min_points: e.target.value })} />
            <input className="input-dark" placeholder="Unlock slug (optional)" value={v.unlock_slug || ""}
                   onChange={(e) => setV({ ...v, unlock_slug: e.target.value })} />
            <input className="input-dark" placeholder="Duration (e.g. 3:24)" value={v.duration || ""}
                   onChange={(e) => setV({ ...v, duration: e.target.value })} />
            <input className="input-dark" type="date" value={v.release_date || ""}
                   onChange={(e) => setV({ ...v, release_date: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-white text-sm">
            <input type="checkbox" checked={v.is_visible !== false}
                   onChange={(e) => setV({ ...v, is_visible: e.target.checked })} /> Visible to members
          </label>
          <div className="flex justify-end">
            <button onClick={() => onSaveMeta(v)} data-testid="vault-save-meta-btn"
                    className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
              <Save className="w-3.5 h-3.5" /> {v.id ? "Save Details" : "Create & Enable Uploads"}
            </button>
          </div>
        </div>

        {/* Media dropzone */}
        <div className="border-t border-white/5 pt-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Media Files</p>
          <div
            data-testid="vault-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => v.id && inputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              !v.id ? "border-white/5 text-white/20 cursor-not-allowed"
              : dragOver ? "border-[#D4AF37] bg-[#D4AF37]/5 text-[#D4AF37]"
              : "border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            }`}
          >
            <Upload className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">{v.id ? "Drop files or click to upload" : "Save the item first to enable uploads"}</p>
            <p className="text-[10px] mt-1 text-white/40">MP3, WAV, MP4, MOV, PDF, ZIP, images — anything up to a few hundred MB</p>
            <input ref={inputRef} type="file" multiple hidden onChange={(e) => e.target.files && uploadFiles(e.target.files)}
                   accept="audio/*,video/*,image/*,application/pdf,application/zip,.mp3,.wav,.flac,.mp4,.mov,.pdf,.zip,.png,.jpg,.jpeg,.webp" />
          </div>

          {uploading.length > 0 && (
            <div className="mt-3 space-y-1">
              {uploading.map((u) => (
                <div key={u.id} className="flex items-center gap-2 text-xs text-white/60">
                  <div className="w-3 h-3 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                  <span className="flex-1 truncate">{u.name}</span>
                  <span>{humanSize(u.size)}</span>
                </div>
              ))}
            </div>
          )}

          {(v.files || []).length > 0 && (
            <div className="mt-4 space-y-2" data-testid="vault-files-list">
              {(v.files || []).map((f) => {
                const Icon = KIND_ICON[f.kind] || FileIcon;
                const previewUrl = f.storage_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.storage_path}` : "";
                return (
                  <div key={f.id} className="bg-black border border-white/10 rounded p-3" data-testid={`vault-file-${f.id}`}>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm truncate">{f.original_filename}</div>
                        <div className="text-white/40 text-[11px]">{f.kind} · {humanSize(f.size)} · {f.mime_type}</div>
                      </div>
                      {f.kind === "image" && (
                        <button onClick={() => setAsCover(f)} data-testid={`set-cover-${f.id}`}
                                className="text-[10px] uppercase tracking-widest text-white/50 hover:text-[#D4AF37]">
                          Set Cover
                        </button>
                      )}
                      <button onClick={() => removeFile(f.id)} data-testid={`remove-file-${f.id}`}
                              className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {/* Inline preview */}
                    {f.kind === "image" && previewUrl && (
                      <img src={previewUrl} alt="" className="mt-3 max-h-40 rounded" />
                    )}
                    {f.kind === "audio" && previewUrl && (
                      <audio controls src={previewUrl} className="w-full mt-3" />
                    )}
                    {f.kind === "video" && previewUrl && (
                      <video controls src={previewUrl} className="w-full mt-3 rounded max-h-64" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
